import React from 'react';
import { Target, Eye, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Card } from '../components/ui/card';

const About = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1770009971150-f50bc7d373a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MDQwMzc2Nnww&ixlib=rb-4.1.0&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">
            {t.about.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 font-light">
            {t.about.subtitle}
          </p>
        </div>
      </section>

      {/* Main Description */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed text-center mb-16">
            {t.about.description}
          </p>

          {/* Mission, Vision, Team Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 border-gray-200 hover:shadow-xl transition-shadow duration-300 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t.about.mission.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t.about.mission.description}
                </p>
              </div>
            </Card>

            <Card className="p-8 border-gray-200 hover:shadow-xl transition-shadow duration-300 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t.about.vision.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t.about.vision.description}
                </p>
              </div>
            </Card>

            <Card className="p-8 border-gray-200 hover:shadow-xl transition-shadow duration-300 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t.about.team.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t.about.team.description}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative h-96 overflow-hidden rounded-lg shadow-lg group">
              <img
                src="https://images.unsplash.com/photo-1643193855567-df3430740538?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MDQwMzc2Nnww&ixlib=rb-4.1.0&q=85"
                alt="Architecture workspace"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="relative h-96 overflow-hidden rounded-lg shadow-lg group">
              <img
                src="https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzcwNDAzNzgwfDA&ixlib=rb-4.1.0&q=85"
                alt="Modern building"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {t.contact.cta}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.contact.subtitle}
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
